import { Download, RotateCw, Upload } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { Badge, Button, Label, Slider } from "~/components/ui";
import { useGeoreferenceIO } from "~/features/georeferencer/hooks";
import { calculateInitialViewState, type GCP } from "~/features/georeferencer/utils";
import { OverlayMap } from "~/lib/map/components/OverlayMap";
import type { ResolutionType } from "~/lib/map/types";
import { type Gdal, georeference, initGdal } from "~/utils/geotiff";
import { GCPTable } from "./GCPTable";
import { GeoreferenceMapViewer } from "./GeoreferenceMapViewer";
import { ImageUploader } from "./ImageUploader";
import { ImageViewer } from "./ImageViewer";

// gdal3.js streams ~40MB of wasm + data from the CDN on first load, which routinely
// takes well over a few seconds on a cold cache. This is only a last-resort safety net
// for a genuinely stuck load — a late resolve still recovers via the .then below. (V1
// used 3s here, which flagged a normal cold load as "failed" while it was still downloading.)
const GDAL_INIT_TIMEOUT_MS = 60_000;
const RESOLUTION: ResolutionType = 3;

export function GeoreferencerContainer() {
  const { t } = useTranslation();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [gcps, setGcps] = useState<GCP[]>([]);
  const [selectedImagePoint, setSelectedImagePoint] = useState<{ x: number; y: number } | null>(null);
  const [selectedGcpId, setSelectedGcpId] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const [geotiffResult, setGeotiffResult] = useState<Blob | null>(null);
  const [geotiffResultUrl, setGeotiffResultUrl] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [overlayOpacity, setOverlayOpacity] = useState(0.1);

  const [gdal, setGdal] = useState<Gdal | null>(null);
  const [gdalInitializing, setGdalInitializing] = useState(true);
  const [gdalInitFailed, setGdalInitFailed] = useState(false);
  const [viewerHeight, setViewerHeight] = useState(40);

  const {
    exportGcpsAsJson,
    importGcpsFromJson,
    handleDownloadGeoTIFF,
    fileInputRef,
    error: ioError,
  } = useGeoreferenceIO({ gcps, setGcps, imageFile, geotiffResult });

  const error = ioError || localError;

  const performGdalInit = useCallback(() => {
    const timeoutId = setTimeout(() => {
      setLocalError(t("feature.georeferencer.errors.gdalInitFailed"));
      setGdalInitFailed(true);
      setGdal(null);
      setGdalInitializing(false);
    }, GDAL_INIT_TIMEOUT_MS);

    initGdal()
      .then((gdalInstance) => {
        clearTimeout(timeoutId);
        setGdal(gdalInstance);
        setGdalInitFailed(false);
        setGdalInitializing(false);
      })
      .catch(() => {
        clearTimeout(timeoutId);
        setLocalError(t("feature.georeferencer.errors.gdalInitFailed"));
        setGdalInitFailed(true);
        setGdal(null);
        setGdalInitializing(false);
      });
  }, [t]);

  const handleRetry = useCallback(() => {
    setGdalInitializing(true);
    setGdalInitFailed(false);
    setLocalError(null);
    performGdalInit();
  }, [performGdalInit]);

  useEffect(() => {
    performGdalInit();
  }, [performGdalInit]);

  useEffect(() => {
    if (gcps.length >= 3 && imageFile && gdal) {
      void (async () => {
        try {
          const result = await georeference(gdal, imageFile, gcps);
          setGeotiffResult(result.blob);
          setGeotiffResultUrl(result.url);
          setShowResult(true);
          setLocalError(null);
        } catch {
          setLocalError(t("feature.georeferencer.errors.processingError"));
        }
      })();
    }
  }, [gcps, imageFile, gdal, t]);

  const resetSelections = () => {
    setSelectedImagePoint(null);
    setSelectedGcpId(null);
  };

  const handleImageUpload = (file: File) => {
    setImageUrl(URL.createObjectURL(file));
    setImageFile(file);
    setGcps([]);
    resetSelections();
    setGeotiffResult(null);
    setGeotiffResultUrl(null);
    setShowResult(false);
    setLocalError(null);
  };

  const handleImageClick = (x: number, y: number) => {
    setSelectedImagePoint({ x, y });
    setSelectedGcpId(null);
  };

  const handleMapClick = (latitude: number, longitude: number) => {
    if (!selectedImagePoint) return;
    setGcps([
      ...gcps,
      {
        id: uuidv4(),
        sourceX: selectedImagePoint.x,
        sourceY: selectedImagePoint.y,
        targetX: longitude,
        targetY: latitude,
      },
    ]);
    resetSelections();
  };

  const handleGcpSelect = (gcpId: string) => {
    setSelectedGcpId(gcpId);
    setSelectedImagePoint(null);
  };

  const handleRemoveGcp = (id: string) => {
    setGcps(gcps.filter((gcp) => gcp.id !== id));
    if (selectedGcpId === id) setSelectedGcpId(null);
  };

  const handleClearAllGcps = () => {
    setGcps([]);
    resetSelections();
  };

  const mapInitialViewState = useMemo(() => calculateInitialViewState(gcps), [gcps]);

  return (
    <div className="space-y-4 p-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <h1 className="text-dewey-title capitalize text-fg-1">{t("ui.georeferencer")}</h1>
          {gdalInitFailed && !gdalInitializing ? (
            <Button type="button" variant="outline" size="sm" onClick={handleRetry}>
              <RotateCw />
              <span>{t("feature.georeferencer.gdal.failed")}</span>
            </Button>
          ) : (
            <Badge tone={gdal ? "status-approved" : "default"}>
              {gdalInitializing ? t("feature.georeferencer.gdal.initializing") : t("feature.georeferencer.gdal.ready")}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Label htmlFor="viewer-height" className="text-12-bold text-fg-2">
            {t("ui.viewerHeight")}
          </Label>
          <Slider
            id="viewer-height"
            min={20}
            max={80}
            step={5}
            value={viewerHeight}
            onChange={(e) => setViewerHeight(Number(e.target.value))}
            className="w-48"
            aria-label={t("ui.viewerHeight")}
          />
          <span className="w-12 text-12-bold tabular-nums text-fg-1">{`${viewerHeight}vh`}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div style={{ height: `${viewerHeight}vh` }}>
          {imageUrl ? (
            <ImageViewer
              imageUrl={imageUrl}
              onImageClick={handleImageClick}
              selectedPoint={selectedImagePoint}
              gcps={gcps}
              selectedGcpId={selectedGcpId}
            />
          ) : (
            <ImageUploader onImageUpload={handleImageUpload} />
          )}
        </div>
        <div style={{ height: `${viewerHeight}vh` }}>
          <GeoreferenceMapViewer
            onMapClick={handleMapClick}
            gcps={gcps}
            selectedGcpId={selectedGcpId}
            onGcpSelect={handleGcpSelect}
          />
        </div>
      </div>

      {imageUrl && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
              <Upload />
              <span>{t("ui.importX", { x: t("ui.gcps") })}</span>
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={exportGcpsAsJson} disabled={gcps.length === 0}>
              <Download />
              <span>{t("ui.exportX", { x: t("ui.gcps") })}</span>
            </Button>
            <input ref={fileInputRef} type="file" accept=".json" className="hidden" onChange={importGcpsFromJson} />
          </div>

          {error && <p className="text-14-regular text-danger">{error}</p>}

          <GCPTable
            gcps={gcps}
            onRemoveGcp={handleRemoveGcp}
            onClearAll={handleClearAllGcps}
            selectedGcpId={selectedGcpId}
            onGcpSelect={handleGcpSelect}
          />

          {showResult && geotiffResultUrl && (
            <div className="space-y-3">
              <h2 className="text-16-bold capitalize text-fg-1">{t("ui.resultX", { x: t("ui.georeferencer") })}</h2>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Label htmlFor="opacity-slider" className="text-12-bold text-fg-2">
                    {t("ui.opacity")}
                  </Label>
                  <Slider
                    id="opacity-slider"
                    min={0}
                    max={1}
                    step={0.1}
                    value={overlayOpacity}
                    onChange={(e) => setOverlayOpacity(Number(e.target.value))}
                    className="w-48"
                  />
                  <span className="w-8 text-12-bold tabular-nums text-fg-1">{overlayOpacity.toFixed(1)}</span>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={handleDownloadGeoTIFF}>
                  <Download />
                  <span>{t("ui.downloadX", { x: t("ui.geoTIFF") })}</span>
                </Button>
              </div>
              <OverlayMap
                height={500}
                resolution={RESOLUTION}
                areaH3Indexes={[]}
                overlayImageUrls={[geotiffResultUrl]}
                overlayOpacity={overlayOpacity}
                dragOverlayImageMode={false}
                onHexagonClick={() => {}}
                initialViewState={mapInitialViewState}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
